import { api } from '@/lib/api';
import type { ChatSession } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

interface InlineRenameInputProps {
  session: ChatSession;
  onDone: () => void;
  className?: string;
}

export function InlineRenameInput({ session, onDone, className }: InlineRenameInputProps) {
  const qc = useQueryClient();
  const [value, setValue] = useState(session.title || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  const mutation = useMutation({
    mutationFn: (title: string) => api.chatRenameSession(session.id, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['chat', 'session', session.id] });
    },
  });

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = value.trim();
    if (trimmed.length > 0 && trimmed !== session.title) {
      mutation.mutate(trimmed);
    }
    onDone();
  };

  const cancel = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    onDone();
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      onBlur={commit}
      maxLength={200}
      className={
        className ??
        'w-full bg-transparent text-sm text-foreground outline-none border-b border-accent'
      }
    />
  );
}
